import { FormControl, MenuItem, TextField } from '@mui/material';
import { MetricType } from '@strength/domain';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkoutContext } from '../../context/WorkoutContext';
import { IncrementIconButton } from '../tablet-inputs/IncrementIconButton';
import { SetMetricInput } from './types';

export const TabletMetricInput = ({
  metricType,
  setIndex,
  metricId,
  input,
  onEditSetMetric,
  onBlur,
}: {
  metricType: MetricType;
  setIndex: number;
  metricId: string;
  input: SetMetricInput;
  onEditSetMetric: (setIndex: number, value: SetMetricInput) => void;
  onBlur: (
    valueWithUnits: string,
    setIndex: number,
    metricId: string,
    metricType: MetricType,
  ) => void;
}) => {
  const handleEditSetMetric = (setIndex: number, newValue: string) => {
    onEditSetMetric(setIndex, {
      setIndex,
      value: newValue,
      unit: '',
    });
  };

  const handleBlur = () => {
    onBlur(`${input.value}`, setIndex, metricId, metricType);
  };

  switch (metricType) {
    case 'rpe':
      return (
        <IncrementInput
          value={input.value}
          onChange={(newValue) => handleEditSetMetric(setIndex, newValue)}
          onBlur={handleBlur}
          increment={0.5}
          min={1}
          max={10}
          error={input.error}
        />
      );
    default:
      return (
        <BasicMetricInput
          setIndex={setIndex}
          metricId={metricId}
          value={input.value}
          displayUnit={input.unit ?? ''}
          error={input.error}
          metricType={metricType}
          onEditSetMetric={onEditSetMetric}
          onBlur={onBlur}
        />
      );
  }
};

const BasicMetricInput = ({
  setIndex,
  metricId,
  value,
  displayUnit,
  metricType,
  error,
  onEditSetMetric,
  onBlur,
}: {
  value: string;
  displayUnit: string;
  setIndex: number;
  metricId: string;
  metricType: MetricType;
  error?: string;
  onEditSetMetric: (setIndex: number, value: SetMetricInput) => void;
  onBlur: (
    valueWithUnits: string,
    setIndex: number,
    metricId: string,
    metricType: MetricType,
  ) => void;
}) => {
  // Per the athlete's team (resolved in WorkoutProvider).
  const { getMetric, getUnitsForMetric } = useWorkoutContext();
  const { t } = useTranslation();

  const orderedUnits = useMemo(() => {
    return getUnitsForMetric(metricId);
  }, [metricId, getUnitsForMetric]);

  const keywords = useMemo(() => {
    return getMetric(metricId).config.keywords;
  }, [metricId, getMetric]);

  // A metric with no numeric type only accepts its keywords, so pick from a
  // dropdown rather than typing a value.
  if (getMetric(metricId).config.numericType === 'none') {
    return (
      <FormControl className="flex items-center flex-row gap-1" fullWidth>
        <TextField
          select
          id={`keyword-select-${setIndex}-${metricId}`}
          value={value ?? ''}
          sx={{
            mt: 0,
          }}
          InputProps={{
            classes: {
              root: error ? 'bg-red-100' : '',
            },
            sx: {
              width: '100%',
              // Wide enough that the blank value doesn't collapse the select
              // narrower than the longest "None" placeholder text.
              minWidth: 90,
            },
          }}
          onChange={(e) => {
            onEditSetMetric(setIndex, {
              setIndex: Number(setIndex),
              value: e.target.value,
              unit: '',
            });
          }}
          onBlur={() => onBlur(value ?? '', setIndex, metricId, metricType)}
        >
          <MenuItem value="">
            <em>{t('none')}</em>
          </MenuItem>
          {keywords.map((keyword) => (
            <MenuItem key={`${setIndex}-${metricId}-${keyword}`} value={keyword}>
              {keyword}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  }

  return (
    <FormControl className="flex items-center flex-row gap-1">
      <TextField
        onBlur={() =>
          onBlur(`${value}${displayUnit}`, setIndex, metricId, metricType)
        }
        value={value ?? '0'}
        InputProps={{
          classes: {
            root: error ? 'bg-red-100' : '',
          },
          sx: {
            mt: -3,
            width: '100%',
            '& input': {
              minWidth: `${String(value).length}ch`,
            },
          },
          inputMode: 'numeric',
        }}
        onChange={(e) => {
          onEditSetMetric(setIndex, {
            setIndex: Number(setIndex),
            value: e.target.value,
            unit: displayUnit,
          });
        }}
      />
      {orderedUnits.length > 0 && (
        <TextField
          select
          id={`unit-select-${setIndex}-${metricId}`}
          disabled={orderedUnits.length <= 1}
          value={displayUnit}
          onChange={(e) => {
            onEditSetMetric(setIndex, {
              setIndex: Number(setIndex),
              value: value,
              unit: e.target.value,
            });
          }}
          sx={{
            mt: 0,
            width: '70px',
            minWidth: 'fit-content',
          }}
        >
          {orderedUnits.map((unit) => (
            <MenuItem key={`${setIndex}-${metricId}-${unit}`} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </TextField>
      )}
    </FormControl>
  );
};

// basic increment input with no extra chips and such.
const IncrementInput = ({
  min,
  max,
  increment = 1,
  value,
  error,
  onChange,
  onBlur,
}: {
  min?: number;
  max?: number;
  increment?: number;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) => {
  const handleValueChange = (newValue: number) => {
    if (min !== undefined && newValue < min) {
      newValue = min;
    }
    if (max !== undefined && newValue > max) {
      newValue = max;
    }
    onChange(String(newValue));
  };
  return (
    <div className="flex flex-row gap-1">
      <IncrementIconButton
        disabled={min !== undefined && Number(value) <= min}
        onClick={() => handleValueChange(Number(value) - increment)}
        isIncrement={false}
      />
      <TextField
        value={String(value)}
        InputProps={{
          classes: {
            root: error ? 'bg-red-100' : '',
          },
          sx: {
            height: `44px`,
            minWidth: 90,
            width: 'fit-content',
            '& input': {
              minWidth: `${String(value).length}ch`,
            },
          },
          inputMode: 'numeric',
        }}
        onChange={(e) => {
          const newValue = parseInt(e.target.value) || 0;
          handleValueChange(newValue);
        }}
        onBlur={onBlur}
      />
      <IncrementIconButton
        disabled={max !== undefined && Number(value) >= max}
        onClick={() => handleValueChange(Number(value) + increment)}
      />
    </div>
  );
};
